import React from 'react';
import { Box } from '@mafc/trolly';
import COLORS from 'theme/colors';
import { AppliedVoucher, LoyaltyVoucher } from 'network/response/ICart';
import CardDivider from 'pages/Checkout/components/CheckoutOrderSummary/snippets/CardDivider';
import OrderSummaryRow from 'common/OrderSummary/components/OrderSummaryRow';
import useApigeeLabels from 'common/ApigeeLabels/hooks/useApigeeLabels';
import SummaryRowTypes from 'common/OrderSummary/components/OrderSummaryRow/row.types';
import useLoyaltyPointType from 'pages/Checkout/components/LoyaltyPoints/hooks/useLoyaltyPointType.hook';
import { IFinalPrice } from 'utils/price.util';
import { useFeature } from '@mafc/appshell';

const DiscountOrderSummary: React.FC<{
  appliedVouchers: AppliedVoucher[];
  loyaltyVouchers: LoyaltyVoucher[];
  totalAppliedDiscounts: IFinalPrice;
  totalBundleDiscount?: IFinalPrice;
  showHeader?: boolean;
}> = ({
  appliedVouchers,
  loyaltyVouchers,
  totalAppliedDiscounts,
  showHeader = true,
  totalBundleDiscount = {},
}) => {
  const { isShare } = useLoyaltyPointType();

  const {
    discountsSavings,
    bundleDiscount,
    myClubPointsRedeemed,
    promoCodeApplied,
    sharePointsRedeemed,
  } = useApigeeLabels();

  const isCartPromoBundleEnabled = useFeature('common.cart_bundling.enabled').on;

  const isBundlingEnabled = isCartPromoBundleEnabled && (totalBundleDiscount as IFinalPrice).value;

  const unAppropriatedAppliedVouchers = appliedVouchers.filter(
    voucher => !voucher.isVoucherAppropriated
  );

  const isDiscountAvailable =
    unAppropriatedAppliedVouchers.length > 0 || loyaltyVouchers.length > 0 || isBundlingEnabled;

  return isDiscountAvailable ? (
    <>
      <Box mb={12}>
        {showHeader && (
          <OrderSummaryRow
            label={discountsSavings}
            testId="discount-saving-heading"
            variant={SummaryRowTypes.header}
            priceDetails={totalAppliedDiscounts}
            isDiscount
          />
        )}

        {isBundlingEnabled && (
          <OrderSummaryRow
            label={bundleDiscount}
            testId="bundles-discount-saving"
            priceDetails={totalBundleDiscount as IFinalPrice}
            isDiscount
          />
        )}

        {loyaltyVouchers.map(
          ({ voucherCode, currencyValue: { currencyIso, currencyName, value } }) => {
            return (
              <OrderSummaryRow
                label={isShare ? sharePointsRedeemed : myClubPointsRedeemed}
                priceDetails={{
                  value,
                  currencyIso,
                  currencyName,
                }}
                testId={`loyalty-redeemed-${voucherCode}`}
                key={voucherCode}
                isDiscount
              />
            );
          }
        )}

        {unAppropriatedAppliedVouchers.map(
          ({ code, value, currency: { isocode: currencyIso, name: currencyName } }) => {
            return (
              <OrderSummaryRow
                label={promoCodeApplied}
                priceDetails={{
                  value,
                  currencyIso,
                  currencyName,
                }}
                testId={`applied-voucher${code}`}
                key={code}
                isDiscount
              />
            );
          }
        )}
      </Box>

      <CardDivider mb="12px" width={1} bg={COLORS.MERCURY} />
    </>
  ) : (
    <></>
  );
};

export default DiscountOrderSummary;
